<script setup>
import {Autoplay, Navigation, Pagination} from "swiper/modules";
import {Swiper, SwiperSlide} from "swiper/vue";
import {useBaseStore} from "~/stores/base";
import star10Percent from "assets/img/icon-star-10-percent.svg";
import star20Percent from "assets/img/icon-star-20-percent.svg";
import star30Percent from "assets/img/icon-star-30-percent.svg";
import star40Percent from "assets/img/icon-star-40-percent.svg";
import star50Percent from "assets/img/icon-star-50-percent.svg";
import star60Percent from "assets/img/icon-star-60-percent.svg";
import star70Percent from "assets/img/icon-star-70-percent.svg";
import star80Percent from "assets/img/icon-star-80-percent.svg";
import star90Percent from "assets/img/icon-star-90-percent.svg";
import star100Percent from "assets/img/icon-star-filled.svg";

const router = useRouter()
const route = useRoute()

/** props **/
const props = defineProps({
  reviews: {
    default: () => [],
    required: true
  }
})

/** data **/
const swiperInstance = ref(null);

/** computed **/
const isMobile = computed(() => useBaseStore().getterIsMobile)
const reviewsLocal = computed(() => props.reviews.map(review => {
  const fullStars = Math.floor(review.score || 0)
  const hasHalfStar = review.score % 1 >= 0.5
  const halfStarImg = () => {
    const percent = Math.round(review.score % 1 * 10) * 10
    switch (percent) {
      case 10:
        return star10Percent
      case 20:
        return star20Percent
      case 30:
        return star30Percent
      case 40:
        return star40Percent
      case 50:
        return star50Percent
      case 60:
        return star60Percent
      case 70:
        return star70Percent
      case 80:
        return star80Percent
      case 90:
        return star90Percent
      case 100:
        return star100Percent
    }
  }
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0)

  return {
    ...review,
    fullStars,
    hasHalfStar,
    halfStarImg,
    emptyStars
  }
}))

/** watch **/

/** methods **/
const onSwiper = (swiper) => {
  swiperInstance.value = swiper; // 保存 Swiper 实例
};
const onSlideChange = () => {
};
const toRecommend = id => {
  router.replace({
    path: router.currentRoute.value.path,
    query: router.currentRoute.value.query, // 保留查询参数
    hash: `#reviews-recommend-${id}`
  });
}
const handleViewAll = () => {
  router.replace({
    path: router.currentRoute.value.path,
    query: router.currentRoute.value.query, // 保留查询参数
    hash: '#reviews-all'
  });
}
</script>

<template>
  <div class="module-product-reviews-mobile" v-if="isMobile">
    <div class="carousel">
      <template v-for="(review, index) in reviewsLocal">
        <!-- 图片评论 -->
        <img class="pic" :src="review.imageSets[0].url" alt="image" @click="toRecommend(review.id)"
             v-if="review.imageSets.length">
        <!-- 文字评论 -->
        <div class="text" @click="handleViewAll" v-else>
          <div class="rate">
            <div class="stars">
              <img src="@/assets/img/icon-star-filled.svg" alt="star" v-for="star in review.fullStars">
              <img :src="review.halfStarImg" alt="star" v-if="review.hasHalfStar">
              <img src="@/assets/img/icon-star-empty.svg" alt="star" v-for="star in review.emptyStars">
            </div>
            <div class="createTime">
              {{ review.createTime }}
            </div>
          </div>
          <div class="title">
            {{ review.title }}
          </div>
          <div class="description">
            {{ review.description }}
          </div>
        </div>
      </template>
    </div>
    <div class="btn" @click="handleViewAll">VIEW ALL</div>
  </div>
  <div class="module-product-reviews-desktop" v-else>
    <div class="btn" @click="handleViewAll">VIEW ALL</div>
    <swiper
      :loop="reviewsLocal.length > 3"
      grab-cursor
      :autoplay="false"
      :navigation="{
          prevEl: '.navigation-left',
          nextEl: '.navigation-right'
        }"
      :modules="[Navigation, Pagination]"
      :slides-per-view="'auto'"
      :slides-per-group="1"
      :space-between="10"
      @swiper="onSwiper"
      @slideChange="onSlideChange"
    >
      <swiper-slide v-for="(review, index) in reviewsLocal">
        <!-- 图片评论 -->
        <img class="pic" :src="review.imageSets[0].url" alt="image" @click="toRecommend(review.id)"
             v-if="review.imageSets.length">
        <!-- 文字评论 -->
        <div class="text" @click="handleViewAll" v-else>
          <div class="rate">
            <div class="stars">
              <img src="@/assets/img/icon-star-filled.svg" alt="star" v-for="star in review.fullStars">
              <img :src="review.halfStarImg" alt="star" v-if="review.hasHalfStar">
              <img src="@/assets/img/icon-star-empty.svg" alt="star" v-for="star in review.emptyStars">
            </div>
            <div class="createTime">
              {{ review.createTime }}
            </div>
          </div>
          <div class="title">
            {{ review.title }}
          </div>
          <div class="description">
            {{ review.description }}
          </div>
        </div>
      </swiper-slide>

      <!-- If we need navigation buttons -->
      <div class="navigation-left" v-if="!swiperInstance?.isEnd">
        <img src="@/assets/img/icon-left-small.svg" alt="left">
      </div>
      <div class="navigation-right" v-if="!swiperInstance?.isEnd">
        <img src="@/assets/img/icon-right-small.svg" alt="right">
      </div>
    </swiper>
  </div>
</template>

<style scoped lang="scss">
@import "src/assets/config";

.module-product-reviews-mobile {
  padding: 0 16px;
  margin-top: 16px;

  .carousel {
    margin-top: 16px;
    display: flex;
    gap: 10px;
    overflow-x: scroll;
    position: relative;

    .pic {
      flex-shrink: 0;

      width: 140px;
      height: 140px;
      object-position: center center;
      object-fit: cover;
    }

    .text {
      flex-shrink: 0;

      border: 0.5px $color-gray-300 solid;
      width: 215px;
      height: 142px;
      padding: 10px;

      .rate {
        display: flex;
        align-items: center;
        justify-content: space-between;

        .stars {
          img {
            width: 20px;
            height: 20px;
          }
        }

        .createTime {
          font-family: "TWK Lausanne";
          font-size: 11px;
          font-style: normal;
          font-weight: 300;
          line-height: 18px; /* 163.636% */
        }
      }

      .title {
        // 单行
        word-break: break-all;
        word-wrap: anywhere;
        display: -webkit-box;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-box-orient: vertical;
        line-clamp: 1;
        -webkit-line-clamp: 1;

        margin-top: 8px;

        font-family: "TWK Lausanne";
        font-size: 14px;
        font-style: normal;
        font-weight: 650;
        line-height: 28px; /* 200% */
        text-decoration: none;
      }

      .description {
        // 4行
        word-wrap: anywhere;
        display: -webkit-box;
        overflow: hidden;
        text-overflow: ellipsis;
        -webkit-box-orient: vertical;
        line-clamp: 4;
        -webkit-line-clamp: 4;

        overflow-wrap: break-word; /* 标准属性，现代浏览器推荐 */
        word-break: break-word; /* 提高兼容性（非标准，WebKit 支持） */
        white-space: pre-wrap; /* 保留用户输入的换行符 */

        font-family: "TWK Lausanne";
        font-size: 12px;
        font-style: normal;
        font-weight: 300;
        line-height: 16px; /* 133.333% */
      }
    }
  }

  .btn {
    font-family: "TWK Lausanne";
    font-size: 14.452px;
    font-style: normal;
    font-weight: 650;
    line-height: 18.581px; /* 128.571% */
    letter-spacing: -0.058px;
    text-decoration-line: underline;
    text-transform: uppercase;
    text-align: center;

    margin-top: 10px;
  }
}

.module-product-reviews-desktop {
  width: 600px;

  .btn {
    font-family: "TWK Lausanne";
    font-size: 14.452px;
    font-style: normal;
    font-weight: 650;
    line-height: 18.581px; /* 128.571% */
    letter-spacing: -0.058px;
    text-decoration-line: underline;
    text-transform: uppercase;

    cursor: pointer;
  }

  .swiper {
    margin-top: 16px;
    position: relative;

    &:hover {
      .navigation-left {
        opacity: 1;
      }

      .navigation-right {
        opacity: 1;
      }
    }

    .swiper-slide {
      cursor: pointer;
      width: fit-content;

      .pic {
        width: 145px;
        height: 145px;
        object-position: center center;
        object-fit: cover;
      }

      .text {
        border: 0.5px $color-gray-300 solid;
        width: 295px;
        height: 160px;
        padding: 16px;

        .rate {
          display: flex;
          align-items: center;
          justify-content: space-between;

          .stars {
            img {
              width: 24px;
              height: 24px;
            }
          }

          .createTime {
            /* Text xs/Regular */
            font-family: "TWK Lausanne";
            font-size: 12px;
            font-style: normal;
            font-weight: 300;
            line-height: 18px; /* 150% */
          }
        }

        .title {
          // 单行
          word-break: break-all;
          word-wrap: anywhere;
          display: -webkit-box;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          line-clamp: 1;
          -webkit-line-clamp: 1;

          margin-top: 6px;
          /* Text lg/Bold */
          font-family: "TWK Lausanne";
          font-size: 18px;
          font-style: normal;
          font-weight: 650;
          line-height: 28px; /* 155.556% */
          text-decoration: none;
        }

        .description {
          // 3行
          word-wrap: anywhere;
          display: -webkit-box;
          overflow: hidden;
          text-overflow: ellipsis;
          -webkit-box-orient: vertical;
          line-clamp: 3;
          -webkit-line-clamp: 3;

          overflow-wrap: break-word; /* 标准属性，现代浏览器推荐 */
          word-break: break-word; /* 提高兼容性（非标准，WebKit 支持） */
          white-space: pre-wrap; /* 保留用户输入的换行符 */

          /* Text sm/Regular */
          font-family: "TWK Lausanne";
          font-size: 14px;
          font-style: normal;
          font-weight: 300;
          line-height: 20px; /* 142.857% */
        }
      }
    }

    .navigation-left {
      position: absolute;
      display: flex;
      left: 0;
      top: 50%;
      z-index: 1;
      transform: translate(0, -50%);
      cursor: pointer;

      transition: all .1s ease-in-out;
      opacity: 0;

      img {
        width: 31px;
        height: 31px;
      }
    }

    .navigation-right {
      position: absolute;
      display: flex;
      right: 0;
      top: 50%;
      z-index: 1;
      transform: translate(0, -50%);
      cursor: pointer;

      transition: all .1s ease-in-out;
      opacity: 0;

      img {
        width: 31px;
        height: 31px;
      }
    }
  }
}
</style>
